/**
 * Shared trial CTA flow (Stripe card-required trial) — supports Pro AND Lite.
 *
 * Used by HeroShell (home), TaaftHeroShell (/taaft), PricingSection (/plans Pro
 * + Lite cards), TaaftPricingSection + TaaftPricingWhite, TaaftMinimalNav, and
 * TaaftTrialInfoModal so every "Start Free Trial" button behaves consistently.
 *
 * MODEL (replaces the old no-credit-card /billing/start-trial path):
 *
 *   - Logged-OUT user: navigates to /auth/register?intent=<plan>_trial(_annual).
 *     The plan + billing period are encoded in the signup_intent DB column so
 *     they survive register → verify-email → login even across devices. The
 *     post-auth router (Login.tsx, OnboardingExperience.tsx) reads the intent
 *     and opens Stripe Checkout for that plan.
 *
 *   - Logged-IN free user: opens Stripe Checkout directly for the chosen plan at
 *     the chosen interval, with a 7-day trial. Stripe collects a card up-front.
 *     Add card → trial. Abandon → /platform as free (cancel_url carries
 *     trial_skipped=1 for the soft nudge).
 *
 *   - Already subscribed / mid-trial: no-op into /platform.
 *
 * Plan: the CTA passes plan ('pro' | 'lite'); default 'pro' keeps every existing
 * hero/nav/taaft caller unchanged. Annual vs monthly: ONLY the annual pricing
 * card passes isAnnual=true; every other entry point defaults to monthly.
 */

import type { NavigateFunction } from "react-router-dom";
import { getPlans, getSubscription, redirectToCheckout } from "@/shared/services/billingApi";
// Lightweight wrapper keeps trialFlow off the eager analytics import chain
// (HeroShell → trialFlow was pulling analytics into the main bundle).
import { trackClick } from "@/shared/utils/track";
import { tokenManager } from "@/shared/services/tokenManager";
import { goToBuildAgent } from "@/shared/lib/solutionsUrl";

type ToastFn = (opts: { title: string; description?: string; variant?: "default" | "destructive" }) => void;

/** Days of free trial Stripe applies. Mirrors backend plan_specs (Pro + Lite both 7). */
const TRIAL_DAYS = 7;

/** Plans that offer a 7-day trial via the trial CTA. */
export type TrialPlan = "pro" | "lite";

export type TrialBillingInterval = "month" | "year";

/**
 * localStorage keys for the soft-nudge banner (rendered by AuthenticatedLayout).
 *   PENDING: a free user has an un-acted trial offer — show the banner on /platform.
 *   DONE:    set once the user dismisses or acts on it, so it never re-triggers.
 * Centralised here (not in the layout) so the post-auth routers can flag the
 * nudge when a Checkout redirect fails — turning a silent drop-to-Free into a
 * visible, recoverable prompt.
 */
export const TRIAL_NUDGE_PENDING_KEY = "midash_trial_nudge_pending";
export const TRIAL_NUDGE_DONE_KEY = "midash_trial_nudge_done";

/** Flag the soft nudge as pending unless the user has already dismissed it for good. */
export function flagTrialNudgePending(): void {
  try {
    if (localStorage.getItem(TRIAL_NUDGE_DONE_KEY) !== "1") {
      localStorage.setItem(TRIAL_NUDGE_PENDING_KEY, "1");
    }
  } catch {
    // localStorage unavailable (private mode / SSR) — nudge is best-effort.
  }
}

/**
 * Backup for the trial signup intent. The intent normally rides the register URL
 * (`/auth/register?intent=pro_trial`), but a query param survives only ONE
 * navigation — any bounce between the CTA and the register submit silently drops
 * it. We persist it here at CTA-click time so Register.tsx can fall back to it.
 * 24h TTL mirrors pricingIntent so a stale click can't grant a trial days later.
 */
const TRIAL_INTENT_KEY = "midash_trial_intent";
const TRIAL_INTENT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export type TrialIntentValue =
  | "pro_trial"
  | "pro_trial_annual"
  | "lite_trial"
  | "lite_trial_annual";

const TRIAL_INTENT_VALUES: TrialIntentValue[] = [
  "pro_trial",
  "pro_trial_annual",
  "lite_trial",
  "lite_trial_annual",
];

/** Persist the trial intent at CTA-click time (backup for the URL param). */
export function storeTrialIntent(intent: TrialIntentValue): void {
  try {
    localStorage.setItem(TRIAL_INTENT_KEY, JSON.stringify({ intent, ts: Date.now() }));
  } catch {
    // localStorage unavailable — the URL param remains the primary carrier.
  }
}

/** Read the stored trial intent if present and not expired; null otherwise. */
export function readTrialIntent(): TrialIntentValue | null {
  try {
    const raw = localStorage.getItem(TRIAL_INTENT_KEY);
    if (!raw) return null;
    const { intent, ts } = JSON.parse(raw);
    if (typeof ts !== "number" || Date.now() - ts > TRIAL_INTENT_MAX_AGE_MS) {
      localStorage.removeItem(TRIAL_INTENT_KEY);
      return null;
    }
    return TRIAL_INTENT_VALUES.includes(intent) ? intent : null;
  } catch {
    return null;
  }
}

/** Clear the stored trial intent once registration has consumed it. */
export function clearTrialIntent(): void {
  try {
    localStorage.removeItem(TRIAL_INTENT_KEY);
  } catch {
    // noop
  }
}

/**
 * Fallback price IDs (mirror PricingSection.FALLBACK_PLAN_IDS). Used only if
 * getPlans() is unavailable — normally the live Stripe price IDs come from the API.
 */
const FALLBACK_PRICE: Record<TrialPlan, Record<TrialBillingInterval, string>> = {
  pro: { month: "price_pro_monthly", year: "price_pro_annual" },
  lite: { month: "price_lite_monthly", year: "price_lite_annual" },
};

/** The signal set the trial decision is made from. Built by buildTrialContext(). */
export interface TrialContext {
  /** Which plan's trial the CTA is for. */
  plan: TrialPlan;
  /** Has a valid, non-expired access token. */
  isLoggedIn: boolean;
  /** Did the click originate from the ANNUAL pricing card? */
  isAnnual: boolean;
  /** Canonical tier slug from /billing/subscription, or null when logged-out/unknown. */
  currentPlan: string | null;
  /** Already on an active trial (app-managed or Stripe). */
  isTrialing: boolean;
}

/** The resolved next step. dispatchTrialAction() executes one of these. */
export type TrialAction =
  | { kind: "register"; intent: TrialIntentValue }
  | { kind: "checkout"; plan: TrialPlan; interval: TrialBillingInterval }
  | { kind: "noop"; reason: string };

/**
 * Tiers that already carry a (Stripe) subscription. A user on any of these must
 * NOT be sent through Checkout again — see resolveTrialAction for why. Includes
 * 'lite' now that it is a real paid tier.
 */
const SUBSCRIBED_TIERS = new Set(["lite", "pro", "pro_plus", "byoai"]);

/**
 * True when the user already holds a paid subscription or is mid-trial — i.e. we
 * must NOT auto-open a new Stripe Checkout for them. A fresh Checkout session
 * always creates a NEW subscription, so routing an already-subscribed user into
 * one would double-bill; plan changes go through the billing portal instead.
 *
 * The post-auth routers (Login.tsx, OnboardingExperience.tsx) call this to refuse
 * stale or cross-account funnel intents — the same invariant resolveTrialAction
 * enforces for the CTA path, lifted out so the routers share it.
 */
export function isSubscribedOrTrialing(
  plan: string | null | undefined,
  isTrial: boolean | null | undefined,
): boolean {
  return !!isTrial || (plan != null && SUBSCRIBED_TIERS.has(plan));
}

// ============================================================================
//  CENTRAL DECISION FUNCTION  — the business logic of the trial entry point.
//
//  Intentionally a PURE function (no I/O, no navigation): given the context it
//  returns WHAT should happen. All side-effects live in dispatchTrialAction().
// ============================================================================
export function resolveTrialAction(ctx: TrialContext): TrialAction {
  // (1) Already subscribed or mid-trial → no-op into the app.
  //
  // Correctness guard, not just UX: a trialing/subscribed user already has a
  // Stripe subscription, so a NEW Checkout session would create a SECOND
  // subscription (double billing). Plan changes (e.g. Lite → Pro) must go
  // through the billing portal / change-plan flow, never a fresh Checkout.
  if (ctx.isTrialing || (ctx.currentPlan != null && SUBSCRIBED_TIERS.has(ctx.currentPlan))) {
    return { kind: "noop", reason: "already-subscribed-or-trialing" };
  }

  // (2) Logged-out → defer card collection until after auth. Plan + billing
  // period ride along in the signup_intent value (survives the email-verify gap).
  if (!ctx.isLoggedIn) {
    const annual = ctx.isAnnual;
    const intent: TrialIntentValue =
      ctx.plan === "lite"
        ? (annual ? "lite_trial_annual" : "lite_trial")
        : (annual ? "pro_trial_annual" : "pro_trial");
    return { kind: "register", intent };
  }

  // (3) Logged-in free user (incl. abandoned-checkout re-entry) → Stripe Checkout
  // for the chosen plan with a trial. Interval reflects THIS click's source
  // (annual card → annual, else monthly) — latest action wins, by design.
  return { kind: "checkout", plan: ctx.plan, interval: ctx.isAnnual ? "year" : "month" };
}

interface HandleTrialCTAOptions {
  source: string; // e.g. "hero-shell", "pricing-pro-card", "pricing-lite-card"
  /** Which plan's trial to start. Defaults to "pro" (keeps existing callers unchanged). */
  plan?: TrialPlan;
  navigate: NavigateFunction;
  toast: ToastFn;
  /** True only when the click came from the annual pricing card. Defaults to monthly. */
  isAnnual?: boolean;
  /** Callback invoked before and after async operations (for button loading state). */
  setLoading?: (loading: boolean) => void;
}

function isLoggedInNow(): boolean {
  const token = tokenManager.getAccessToken();
  return !!(token && !tokenManager.isTokenExpired());
}

/**
 * Gather the decision context. For logged-in users this makes one
 * /billing/subscription call so we can short-circuit already-subscribed users
 * and avoid a pointless Checkout round-trip. Degrades to "free" on any failure.
 */
async function buildTrialContext(plan: TrialPlan, isAnnual: boolean): Promise<TrialContext> {
  const isLoggedIn = isLoggedInNow();
  if (!isLoggedIn) {
    return { plan, isLoggedIn: false, isAnnual, currentPlan: null, isTrialing: false };
  }
  try {
    const sub = await getSubscription();
    return {
      plan,
      isLoggedIn: true,
      isAnnual,
      currentPlan: sub.plan ?? null,
      isTrialing: !!sub.is_trial,
    };
  } catch {
    // Billing API unavailable — treat as a free logged-in user and let them try.
    return { plan, isLoggedIn: true, isAnnual, currentPlan: null, isTrialing: false };
  }
}

/**
 * Parse a persisted signup_intent value into its plan + interval, or null if it
 * is not a trial intent. Used by the post-auth routers (Login.tsx,
 * OnboardingExperience.tsx) to decide whether/how to open trial Checkout.
 */
export function parseTrialIntent(
  intent: string | null | undefined,
): { plan: TrialPlan; interval: TrialBillingInterval } | null {
  switch (intent) {
    case "pro_trial": return { plan: "pro", interval: "month" };
    case "pro_trial_annual": return { plan: "pro", interval: "year" };
    case "lite_trial": return { plan: "lite", interval: "month" };
    case "lite_trial_annual": return { plan: "lite", interval: "year" };
    default: return null;
  }
}

/**
 * Open Stripe Checkout for the given plan with a 7-day trial. Shared by the
 * logged-in CTA path and the post-auth routers. Callers override the URLs so
 * the post-login flow can return the user to /onboarding (Checkout runs BEFORE
 * onboarding) while marketing CTAs return to /platform.
 *
 * Navigates away via window.location — nothing after this resolves.
 */
export async function startTrialCheckout(opts: {
  plan: TrialPlan;
  interval: TrialBillingInterval;
  successUrl?: string;
  cancelUrl?: string;
}): Promise<void> {
  const priceId = await resolvePlanPriceId(opts.plan, opts.interval);
  const origin = window.location.origin;
  await redirectToCheckout(priceId, TRIAL_DAYS, {
    successUrl: opts.successUrl ?? `${origin}/platform?trial_started=1`,
    cancelUrl: opts.cancelUrl ?? `${origin}/platform?trial_skipped=1`,
  });
}

/**
 * Resolve the live Stripe price ID for a plan + interval, falling back to the
 * static IDs if the plans API is unavailable.
 */
async function resolvePlanPriceId(plan: TrialPlan, interval: TrialBillingInterval): Promise<string> {
  try {
    const plans = await getPlans();
    const match = plans.find((p) => {
      const n = p.name.toLowerCase();
      const planMatches =
        plan === "lite"
          ? n === "lite" || n === "lite plan"
          : n.startsWith("pro") && !n.includes("+") && !n.includes("plus");
      return planMatches && (p.interval || "month") === interval;
    });
    if (match?.stripe_price_id) return match.stripe_price_id;
  } catch {
    // fall through to fallback
  }
  return FALLBACK_PRICE[plan][interval];
}

/** Execute the resolved action (the only place with side-effects). */
async function dispatchTrialAction(action: TrialAction, navigate: NavigateFunction): Promise<void> {
  switch (action.kind) {
    case "register":
      // Registration is CLOSED (MiDash is at full capacity) — there is no
      // signup funnel to carry an intent into, so the stored-intent step is
      // skipped entirely and the visitor is handed to the Solutions site to
      // have a custom agent built instead.
      //
      // Only the LOGGED-OUT branch changes. Existing subscribers still hit
      // "checkout" / "noop" below and their trial + upgrade paths are
      // untouched. Re-opening signups = restore the two lines above this
      // comment (storeTrialIntent + navigate to /auth/register).
      goToBuildAgent("trial-cta");
      return;
    case "checkout":
      // success → trial started; cancel → free + one-time soft-nudge banner.
      // Defaults land on /platform (already-onboarded / marketing CTAs).
      await startTrialCheckout({ plan: action.plan, interval: action.interval });
      return; // navigates away via window.location
    case "noop":
      navigate("/platform");
      return;
  }
}

export async function handleTrialCTA({
  source,
  plan = "pro",
  navigate,
  toast,
  isAnnual = false,
  setLoading,
}: HandleTrialCTAOptions): Promise<void> {
  trackClick("Start Free Trial", source);

  // Scope the TAAFT promo to the /taaft funnel. `midash_coupon` is set on every
  // /taaft visit and applied by createCheckoutSession to ANY checkout — so it
  // bleeds into a later main-site trial unless we drop it here. A trial CTA from
  // a non-TAAFT source (hero, /plans, in-app) must NOT inherit a stale coupon;
  // /taaft re-sets it on the next visit, and TAAFT CTAs (source contains "taaft")
  // keep it. Without this, anyone who ever opened /taaft gets the discount forever.
  if (!source.toLowerCase().includes("taaft")) {
    try { localStorage.removeItem("midash_coupon"); } catch { /* best-effort */ }
  }

  try {
    setLoading?.(true);
    const ctx = await buildTrialContext(plan, isAnnual);
    const action = resolveTrialAction(ctx);
    await dispatchTrialAction(action, navigate);
  } catch (err: any) {
    const detail = err?.detail || err?.message || "";
    // "already" subscribed/active → just go into the app, not an error.
    if (detail.toLowerCase().includes("already") || detail.toLowerCase().includes("active")) {
      navigate("/platform");
      return;
    }
    // Surface the failure AND leave a recoverable trail: flag the soft nudge so
    // the user gets a "start your trial" prompt on /platform instead of silently
    // sitting on Free.
    flagTrialNudgePending();
    toast({
      title: "Could not start trial",
      description: detail || "Please try again",
      variant: "destructive",
    });
  } finally {
    setLoading?.(false);
  }
}
